/* eslint-disable @typescript-eslint/naming-convention */
import { type Signal, useSignal } from '@preact/signals-react'
/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react'

import { getView } from '@/helpers'
import { appStateActions } from '@/state/actions'

interface AppProps {
  handleChangeCurrent: (current: string[]) => void
  handleChangeOpenKeys: (openKeys: string[]) => void
  handleCollapseOpenDrawer: () => void
  handleToggleCollapsed: () => void
  size: Signal<{
    height?: number
    width?: number
  }>
}

export const useApp = (): AppProps => {
  const isClient = typeof window === 'object'

  const getSize = React.useCallback(
    () => ({
      height: isClient ? window.innerHeight : undefined,
      width: isClient ? window.innerWidth : undefined,
    }),
    [isClient]
  )

  const size = useSignal(getSize())

  React.useEffect(() => {
    if (!isClient) {
      return // No hay necesidad de suscribirse a eventos si no estamos en el cliente
    }

    const onHandleResize = (): void => {
      size.value = getSize()
    }

    window.addEventListener('resize', onHandleResize)

    return () => {
      window.removeEventListener('resize', onHandleResize)
    }
  }, [getSize, isClient])

  const handleToggleAll = (
    width: number | undefined,
    height: number | undefined
  ): void => {
    if (width === undefined || height === undefined) {
      return
    }
    const view = getView(width)
    const collapsed = view !== 'DesktopView'

    appStateActions.toggleAll({ collapsed, height, view })
  }

  React.useEffect(() => {
    handleToggleAll(size.peek().width, size.peek().height)
  }, [size, handleToggleAll])

  const handleToggleCollapsed = (): void => {
    appStateActions.collapseChange()
  }

  const handleChangeCurrent = (current: string[]): void => {
    appStateActions.changeCurrent(current)
  }

  const handleChangeOpenKeys = (openKeys: string[]): void => {
    appStateActions.changeOpenKeys(openKeys)
  }

  const handleCollapseOpenDrawer = (): void => {
    appStateActions.collapseOpenDrawer()
  }

  return {
    handleChangeCurrent,
    handleChangeOpenKeys,
    handleCollapseOpenDrawer,
    handleToggleCollapsed,
    size,
  }
}
